import "./BillList.scss";

import { useAtom } from "jotai";
import compact from "lodash/compact";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Download } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Pagination } from "@/components/Interface/Pagination/Pagination";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { PageNumberComboBox } from "@/components/Layout/Table/PageNumberComboBox/PageNumberComboBox";
import {
  clampPage,
  clampPageSize,
} from "@/components/Layout/Table/PageNumberComboBox/pageSize";
import { Table } from "@/components/Layout/Table/Table";
import { BillListHeader } from "@/components/Portal/Documents/BillList/components/BillListHeader/BillListHeader";
import { BillListRow } from "@/components/Portal/Documents/BillList/components/BillListRow/BillListRow";
import { BillSelectAllCheckbox } from "@/components/Portal/Documents/BillList/components/BillSelectAllCheckbox/BillSelectAllCheckbox";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useHandleError } from "@/hooks/useHandleError";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useQueryParam } from "@/hooks/useQueryParam";
import { getBillFile, useGetDrBillsForCustomer } from "@/services/api";
import { GetDrBillsForCustomerSortField } from "@/services/model";
import { documentsEditStateAtom } from "@/utils/atoms";
import { log } from "@/utils/log";

const sortFields = [
  "Datum",
  "Abrechnungsmonat",
  "Rechnungsnummer",
  "Betrag",
  "Status",
];

export type BillListBillIdForm = {
  billIds: { [key: number]: string | boolean };
};

export const BillList: FC = () => {
  const { t } = useTranslation("portal");
  const [searchParams] = useSearchParams();
  const [isFetchingBillFile, setIsFetchingBillFile] = useState(false);
  const [documentsEdit, setDocumentsEdit] = useAtom(documentsEditStateAtom);
  // Shared with Documents so standalone mobile mode survives layout toggles
  // without remounting this route and dropping selection mid-click.
  const isSelecting = documentsEdit.billsSelecting;
  const setIsSelecting = (selecting: boolean) => {
    setDocumentsEdit((prev) => ({ ...prev, billsSelecting: selecting }));
  };
  const handleError = useHandleError();
  const isMobile = useIsMobile();
  useBodyScrollLock(isSelecting && isMobile);

  const formMethods = useForm<BillListBillIdForm>({
    defaultValues: { billIds: {} },
  });
  const billIds = compact(Object.values(formMethods.watch("billIds")));

  const [billPage, setCurrentBillPage] = useQueryParam("page");

  // Both params are user-editable in the URL and flow straight into the request:
  // a negative page would send `page: -4` and never be corrected by the effect
  // below, and an unlisted pageSize would force an unbounded fetch.
  const currentBillPage = clampPage(billPage || null);
  const defaultPageSize = isMobile ? 5 : 10;
  const currentBillsPerPage = clampPageSize(
    searchParams.get("pageSize"),
    defaultPageSize,
  );
  const currentSortField = searchParams.get("sortField");
  const typedCurrentSortField = sortFields.includes(`${currentSortField}`)
    ? (currentSortField as GetDrBillsForCustomerSortField)
    : undefined;
  const currentSortDirection = searchParams.get("sortDirection");
  const typedCurrentSortDirection =
    currentSortDirection === "asc"
      ? "asc"
      : currentSortDirection === "desc"
        ? "desc"
        : undefined;

  const { data: billOverview, isFetching: isFetchingBills } =
    useGetDrBillsForCustomer(
      {
        page: currentBillPage - 1,
        pageSize: currentBillsPerPage,
        sortField: typedCurrentSortField,
        sortDirection: typedCurrentSortDirection,
      },
      {
        query: {
          refetchOnWindowFocus: false,
          keepPreviousData: true,
        },
      },
    );

  useEffect(() => {
    // If the total number of pages is smaller than the current selected page,
    // (e.g. we are on page 3 but the data has only 2 pages now)
    // reset to the next possible value
    if (
      billOverview?.totalNumberPages &&
      billOverview.totalNumberPages < currentBillPage
    ) {
      setCurrentBillPage(billOverview.totalNumberPages.toString());
    }
  }, [billOverview?.totalNumberPages, currentBillPage, setCurrentBillPage]);

  const downloadSelectedBills = () => {
    setIsFetchingBillFile(true);
    getBillFile({
      billId: billIds.map((id) => Number(id)),
    })
      .then((data) => {
        if (
          data.billFile?.file &&
          data.billFile.documentName &&
          data.billFile.mimeType === "application/zip"
        ) {
          const url = `data:application/zip;base64,${data.billFile?.file}`;
          const link = document.createElement("a");
          link.download = t("bills.zipName", { count: billIds.length });
          link.href = url;
          link.click();
          window.URL.revokeObjectURL(url);
        } else {
          log(
            "Could not save billFile zip, the check for valid object failed.",
          );
        }
      })
      .catch((error) => handleError(error))
      .finally(() => setIsFetchingBillFile(false));
  };

  const billPages = billOverview?.totalNumberPages || 1;
  const isLoading = isFetchingBills || isFetchingBillFile;

  const showLoading = isLoading && !billOverview;

  const currentPageBillIds = compact(
    billOverview?.bills?.map((bill) => bill.id),
  );

  const leaveSelection = () => {
    formMethods.reset({ billIds: {} });
    setIsSelecting(false);
  };

  const headerActions = isSelecting ? (
    <div className="flex shrink-0 items-center gap-3">
      <Button variant="secondary" size="sm" onClick={leaveSelection}>
        {t("bills.cancelSelection")}
      </Button>
      <Button
        dataTestid="bills-download-selected"
        variant="primary"
        size="sm"
        icon={<Download />}
        ariaLabel={t("bills.download", { count: billIds.length })}
        // Button disables itself while loading — without it every further click
        // fires getBillFile again for the same IDs.
        loading={isFetchingBillFile}
        disabled={billIds.length === 0}
        onClick={downloadSelectedBills}
      >
        {`${billIds.length}`}
      </Button>
    </div>
  ) : (
    <Button
      dataTestid="bills-select-multiple"
      variant="primary"
      size="sm"
      onClick={() => setIsSelecting(true)}
    >
      {t("bills.downloadMultiple")}
    </Button>
  );

  // Plain JSX, not inline components: a new component identity on every render
  // makes React unmount and remount the whole subtree on each `billIds` change,
  // which drops focus from the clicked checkbox and re-measures every clamped
  // label.
  const mobileView = (
    <div className="flex flex-col">
      <div className="flex flex-col">
        {billOverview?.bills?.map((bill) => (
          <BillListRow
            key={bill.id}
            bill={bill}
            variant="compact"
            isSelectMultiple={isSelecting}
          />
        ))}
      </div>
      {/* On mobile the two footer controls stack — rows-per-page first,
          then the full-width pagination row. */}
      <div className="flex flex-col gap-4 pt-3">
        <PageNumberComboBox defaultPageSize={defaultPageSize} />
        {billPages > 1 && <Pagination paginationPages={billPages} />}
      </div>
    </div>
  );

  const desktopView = (
    <>
      <Table
        variant="plain"
        hasPaddingBottom={false}
        data-testid="bills-list-table"
        isLoading={isFetchingBills}
      >
        <thead>
          <BillListHeader
            currentPageBillIds={currentPageBillIds}
            isSelecting={isSelecting}
          />
        </thead>
        <tbody>
          {billOverview?.bills?.map((bill) => (
            <BillListRow
              key={bill.id}
              bill={bill}
              isSelectMultiple={isSelecting}
            />
          ))}
        </tbody>
      </Table>
      <div className="flex w-full flex-row items-center justify-between px-4 py-2">
        <PageNumberComboBox defaultPageSize={defaultPageSize} />
        {billPages > 1 && <Pagination paginationPages={billPages} />}
      </div>
    </>
  );

  return (
    <div className="bill-list">
      {showLoading && <LoadingSpinner />}

      <FormProvider {...formMethods}>
        {/* This card uses flat padding on every viewport — unlike the hero
            card above, which uses the responsive `gutter` token.
            While selecting, on mobile only, `bill-list__card--pinned` (see
            BillList.scss) turns the whole card into a fixed panel below the
            mobile header, exactly like PersonalDataForm's edit-mode title
            bar: `__header` stays put, `__body` scrolls inside its own
            clipped region, so a row scrolling past the header vanishes
            behind it instead of needing a background hack to cover it.
            Pins immediately on click - no scroll-into-view needed, since
            `position: fixed` doesn't depend on the current scroll offset. */}
        <div
          className={`bill-list__card flex flex-col rounded-lg bg-surface-accent-ivory p-4 ${
            isSelecting ? "bill-list__card--pinned" : ""
          }`}
        >
          {/* Headline box next to the action buttons, one `gutter` step above
              the table. On mobile — where there is no table header row — the
              select-all checkbox sits at the far right of this row, after
              the buttons.
              `min-w-0` is required for the h2's `truncate` to actually
              engage: nowrap text has no valid break point, so its min-content
              width equals its full rendered width, and a flex item's default
              min-width is content-based - without `min-w-0` this box refuses
              to shrink at all once "Mehrere herunterladen" needs the room,
              pushing the button past the card's edge instead of ellipsizing
              the title (same pattern BillListRow already uses for the
              tariff name). */}
          <div className="bill-list__header mb-6 flex shrink-0 items-center gap-3">
            <div className="min-w-0 flex-1 px-2">
              <h2 className="truncate py-2 text-base font-semibold leading-none tracking-wide text-foreground-primary">
                {t("bills.list.headline")}
              </h2>
            </div>
            {headerActions}
            {isMobile && isSelecting && (
              // Same trailing slot the rows use, so the select-all sits on
              // the same vertical axis as every row checkbox below it.
              <div className="flex w-10 shrink-0 justify-center">
                <BillSelectAllCheckbox
                  currentPageBillIds={currentPageBillIds}
                />
              </div>
            )}
          </div>
          <div className="bill-list__body">
            {isMobile ? mobileView : desktopView}
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
